// Shared motion kit for the landing page (immersive redesign, 2026-09-24).
//
// One place registers the GSAP plugins and answers the three questions every
// animated component has to ask before it moves anything:
//   * does this visitor want reduced motion?  -> show the end state, no tweens
//   * is there a fine pointer (mouse/trackpad)? -> cursor, magnetic and tilt
//     effects only then; a phone has no hover and a finger is not a cursor
//   * is this a small screen?                   -> lighter scenes, no pinning
// Every helper is safe in jsdom (tests) and during SSR-less first render:
// no window, no matchMedia, no WebGL all read as "keep it simple".

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

let registered = false;

/** GSAP with ScrollTrigger and SplitText registered exactly once. */
export function getGsap() {
  if (!registered && typeof window !== 'undefined') {
    // Once, even if it throws (2026-09-25): ScrollTrigger's registration
    // subscribes to a media query, and a matchMedia whose result has no
    // listener methods (a test double; no real browser) makes it throw. That
    // must not take a hover effect's plain tween down with it, and retrying
    // on every call would only throw again.
    registered = true;
    try {
      gsap.registerPlugin(ScrollTrigger, SplitText);
    } catch { /* plugins unavailable here; core tweens still work */ }
  }
  return { gsap, ScrollTrigger, SplitText };
}

function matches(query: string): boolean {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

export const prefersReducedMotion = () => matches('(prefers-reduced-motion: reduce)');

/** A mouse or trackpad that can hover. False on phones, tablets and in tests. */
export const hasFinePointer = () => matches('(hover: hover) and (pointer: fine)');

/** Phone-sized viewport (Tailwind's `md` breakpoint is 768px). */
export const isSmallScreen = () => matches('(max-width: 767px)');

/** WebGL is available (and not in jsdom, which has no canvas context). */
export function supportsWebGL(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

/** Shared eases, so every section moves with the same feel. */
export const EASE = {
  out: 'expo.out',
  inOut: 'power3.inOut',
  soft: 'power2.out',
} as const;

/** Framer Motion equivalents of EASE.out. */
export const FM_EASE_OUT = [0.16, 1, 0.3, 1] as const;
